<template>
  <div class="side-nav-container" data-test="side-nav">
    <div class="side-nav-blackout" :class="{ 'show-blackout': sideNavIsOpen }" />

    <nav
      @mouseleave="closeNav"
      @blur="closeNav"
      :class="{ 'nav-open': sideNavIsOpen }"
      class="side-nav"
      id="side-nav"
    >
      <SideNavMenu
        v-for="menu in allMenus"
        :key="`${menu.name} ${routeSwitch}`"
        :menu-data="menu"
        :side-nav-is-open="sideNavIsOpen"
        :window-height="windowHeight"
        @open-nav="openNav"
        @open-menu="openMenu"
        @close-menu="closeMenu"
      />
    </nav>

    <button
      class="container side-nav-toggle-btn"
      :class="{ show: isTabletOrMobile && sideNavIsOpen }"
      @click="closeNav"
      type="button"
    >
      <div class="burger-menu" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeMount } from "vue";
import { isMobile } from "mobile-device-detect";
import d from "debug";
import { csContext } from "../../../services/cs-context";
import router from "../../../router/router";
import SideNavMenu from "./side-nav-menu.vue";
import {
  buildSideNavSections,
  getTeamNavItems,
  getWatchGroupNavItems,
  getResourcesNavItems,
  getOrganisationNavItems,
  getUserState,
  checkAccreditedFunctionality,
} from "./side-nav.utils";
import { SideNavSections, SideNavOptions, Menu } from "./side-nav.types";

const debug = d("cs.Router");

const props = defineProps<{
  sideNavIsOpen: boolean;
}>();

const emit = defineEmits(["nav-state-changed"]);

const navSections = ref<SideNavSections>();
const allMenus = ref<Menu[]>([]);
const currentOpenMenu = ref<Menu | null>(null);
const windowHeight = ref(window.innerHeight);
const navOptions = ref<SideNavOptions | null>(null);
const routeSwitch = ref(false);
const isTabletOrMobile = ref(isMobile);

function openNav(): void {
  emit("nav-state-changed", true);
}

function closeNav(): void {
  emit("nav-state-changed", false);
}

function openMenu({
  menuToOpenName,
  hideMenu,
}: {
  menuToOpenName: string;
  hideMenu: string;
}): void {
  const menuToOpen = allMenus.value.find((menu) => menu.name === menuToOpenName);
  const menuToHide = allMenus.value.find((menu) => menu.name === hideMenu);
  if (!menuToOpen || !menuToHide) return;
  menuToOpen.isOpen = true;
  menuToHide.isHiddenLeft = true;
  currentOpenMenu.value = menuToOpen;
  currentOpenMenu.value.parentMenu = menuToHide;
}

function closeMenu(): void {
  if (!currentOpenMenu.value) return;

  currentOpenMenu.value.isOpen = false;

  if (currentOpenMenu.value.parentMenu) {
    currentOpenMenu.value = currentOpenMenu.value.parentMenu;
    currentOpenMenu.value.isHiddenLeft = false;
  }
}

function setUserState() {
  navOptions.value = getUserState();
}

function setSideNavSections() {
  if (navOptions.value) {
    navSections.value = buildSideNavSections(navOptions.value);
  }
}

function setAllMenus() {
  if (!navSections.value) return;
  
  const organisationSection = navSections.value.items.find(({ name }) => name === "My Organisations");
  const organisationItems = organisationSection?.items.filter(({ id }) => !!id) || [];
  
  allMenus.value = [
    navSections.value,
    ...navSections.value.items,
    ...navSections.value.items
      .find(({ name }) => name === "My Teams")!
      .items.filter(({ id }) => !!id),
    ...organisationItems,
    ...navSections.value.items.find(({ name }) => name === "Resources")!.items,
  ].map((menu) => ({
    ...menu,
    isHiddenLeft: false,
    isOpen: menu.id === "home",
  }));
}

async function integrateTeamData() {
  const teams = await getTeamNavItems();
  if (!navSections.value) return;

  navSections.value.items[0].items = [...teams, ...navSections.value.items[0].items];
}

async function integratePrismicData() {
  if (!navSections.value) return;

  navSections.value.items.find((i) => i.name === "Resources")!.items = await getResourcesNavItems();
}

async function integrateWatchGroupsData() {
  const watchGroups = csContext.userDocument.groupsWatched;

  if (!navSections.value) return;

  if (watchGroups.length > 0) {
    const watchGroupItems = getWatchGroupNavItems(watchGroups);
    navSections.value.items.find((i) => i.name === "Watch Groups")!.items = watchGroupItems;
  }
}

async function integrateOrganisationData() {
  if (!navSections.value) return;

  const organisationSection = navSections.value.items.find((i) => i.name === "My Organisations");
  if (organisationSection) {
    const organisations = await getOrganisationNavItems();
    organisationSection.items = organisations;
  }
}

async function integrateAccreditedFunctionality() {
  if (!navOptions.value) return;
  
  const { hasAccess, organisations } = await checkAccreditedFunctionality();
  navOptions.value.hasAccreditedFunctionality = hasAccess;
  
  // If accredited functionality is available, add the organisation section and populate it
  if (hasAccess && navSections.value) {
    // Check if organisation section already exists
    const organisationExists = navSections.value.items.find(item => item.name === "My Organisations");
    
    if (!organisationExists) {
      // Find the index of Resources section to insert before it
      const resourcesIndex = navSections.value.items.findIndex(item => item.name === "Resources");
      navSections.value.items.splice(resourcesIndex, 0, {
        name: "My Organisations",
        navClosedImage: require("../../../assets/side-nav/Organisation-icon-reversed.svg"),
        navOpenImage: require("../../../assets/side-nav/Organisation-icon-forward.svg"),
        activePaths: ["/myorganisation"],
        activePriority: 4,
        divider: false,
        items: [],
      });
    }
    
    await integrateOrganisationData();
  }
}

async function handleAsyncActions() {
  await integrateTeamData();
  await integrateWatchGroupsData();
  await integratePrismicData();
  await integrateAccreditedFunctionality();
}

function setWindowHeight() {
  windowHeight.value = window.innerHeight;
}

onBeforeMount(() => {
  setUserState();
  setSideNavSections();
  handleAsyncActions().then((n) => setAllMenus());
  setAllMenus();

  // Methods to adjust window size to enable proper scrolling on mobile.
  window.addEventListener("resize", setWindowHeight);
});

watch(
  () => props.sideNavIsOpen,
  () => {
    document.body.style.overflow = props.sideNavIsOpen ? "hidden" : "initial";
  },
);

watch(
  () => props.sideNavIsOpen,
  () => {
    if (!props.sideNavIsOpen) {
      setAllMenus();
    }
  },
);

onMounted(() => {
  router.beforeResolve((to, from, next) => {
    routeSwitch.value = !routeSwitch.value;
    debug(`Routing to ${to.fullPath} from ${from.fullPath}`, { to, from });
    next();
  });
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../../styles/bootstrap/variables.less";

@scrollbar-color: @btn-disabled-color;

.side-nav-blackout {
  transition: @side-nav-short-transition;
  position: fixed;
  z-index: 10000;
  top: 0;
  left: 0;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  width: 100%;
  opacity: 0;
  visibility: hidden;

  &.show-blackout {
    visibility: visible;
    opacity: 1;
  }
}

.side-nav {
  position: fixed;
  z-index: 11000;
  height: 100vh;
  top: 0;
  left: 0;
  width: @side-nav-collapsed-width;
  background-color: @side-nav-bg-color;
  font-size: 15px;
  transition: @side-nav-long-transition;
  overflow: hidden;
  scrollbar-color: @scrollbar-color @side-nav-bg-color;
}

.side-nav-container {
  button.side-nav-toggle-btn {
    position: fixed;
    z-index: 11000;
    right: 0;
    top: 0;

    @media (max-width: 338px) {
      right: -20px;
    }

    .burger-menu,
    .burger-menu::before,
    .burger-menu::after {
      background: #eee;
      transition: @side-nav-short-transition;
    }

    .burger-menu {
      background: transparent;
    }

    .burger-menu::before {
      transform: rotate(45deg);
    }

    .burger-menu::after {
      transform: rotate(-45deg);
    }

    &.show-side-nav-btn {
      display: block;
    }
  }
}

// Open nav styles:
.nav-open {
  width: @side-nav-full-width;
  box-shadow: 1px 0 5px 0 #000;

  .nav-item-content {
    transform: translateX(15px);
  }

  span,
  .nav-arrow,
  .nav-item-bullet,
  .nested-menu .nav-item-content {
    display: flex;
    visibility: visible;
  }

  .nav-items {
    width: @side-nav-full-width;
  }

  .nav-item-image-container .nav-item-image {
    -webkit-filter: initial;
    filter: initial;
  }
}

@media (max-width: @screen-xxs-max) {
  .side-nav {
    display: none;
  }

  .side-nav-blackout {
    transition: initial;
  }

  .nav-open {
    display: block;
  }
}

// Global styles affecting the side-nav are located in "side-nav.less"
</style>
